import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { PlanLimitError } from '@/types';

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Error detail carried by the 402 `billing:limit-reached` event (may be absent). */
  error: PlanLimitError | null;
  /** Navigate to the billing page to pick a plan. */
  onUpgrade: () => void;
}

/** Copy keyed off the BE error code, with a sensible generic fallback. */
function titleFor(code: string | undefined): string {
  switch (code) {
    case 'NO_ACTIVE_PLAN':
      return 'Unlock this premium feature';
    case 'FEATURE_NOT_IN_PLAN':
      return 'This feature needs an upgrade';
    case 'PLAN_LIMIT_REACHED':
    default:
      return "You've reached your plan limit";
  }
}

export function UpgradeModal({ open, onOpenChange, error, onUpgrade }: UpgradeModalProps) {
  // Message-only: the BE message is already tailored per code ("You've reached
  // your monthly limit for …" / "Your plan doesn't include …"). The BE drops
  // the `details` ({ used, cap }) on the wire, so there's no usage meter to
  // render — fall back to a generic line when no message is present.
  const message =
    error?.message ??
    "You've hit a limit on your current plan. Upgrade to keep creating without interruptions.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-6">
          <div className="mb-1 flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
            <Sparkles className="size-5 text-white" />
          </div>
          <DialogTitle>{titleFor(error?.code)}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Maybe later
          </Button>
          <Button
            onClick={onUpgrade}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
          >
            View plans &amp; upgrade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
